import { FileId, ExcalidrawElement, InitializedExcalidrawImageElement } from "@excalidraw/excalidraw/element/types";
import { ExcalidrawImperativeAPI , BinaryFileData } from "@excalidraw/excalidraw/types";

export const isInitializedImageElement = (
  element: ExcalidrawElement | null,
): element is InitializedExcalidrawImageElement => {
  return !!element && element.type === "image" && !!element.fileId;
};

class ImageFileManager {
  private trackedFiles = new Map<FileId, BinaryFileData>();

  isFileTracked(fileId: FileId): boolean {
    return this.trackedFiles.has(fileId);
  }

  addFile(fileId: FileId, fileData: BinaryFileData): void {
    this.trackedFiles.set(fileId, fileData);
  }

  getFile(fileId: FileId): BinaryFileData | undefined {
    return this.trackedFiles.get(fileId);
  }

  getAllFiles(): Map<FileId, BinaryFileData> {
    return new Map(this.trackedFiles);
  }

  reset(): void {
    this.trackedFiles.clear();
  }
}

export class ImageSynchronizer {
  private fileManager: ImageFileManager;
  private excalidrawAPI: ExcalidrawImperativeAPI;
  private sendMessage: (message: string) => void;

  constructor(
    excalidrawAPI: ExcalidrawImperativeAPI,
    sendMessage: (message: string) => void
  ) {
    this.fileManager = new ImageFileManager();
    this.excalidrawAPI = excalidrawAPI;
    this.sendMessage = sendMessage;
  }

  handleImageFileUpdate(fileId: FileId, fileData: BinaryFileData): void {
    if (!this.fileManager.isFileTracked(fileId)) {
      this.fileManager.addFile(fileId, fileData);
      
      const files = new Map();
      files.set(fileId, fileData);
      this.excalidrawAPI.addFiles(Array.from(files.values()));
    }
  }

  syncNewImages(): void {
    const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
    const imageElements = elements.filter(
      (element) => isInitializedImageElement(element) && !element.isDeleted
    ) as InitializedExcalidrawImageElement[];

    imageElements.forEach((element) => {
      if (!this.fileManager.isFileTracked(element.fileId)) {
        const files = this.excalidrawAPI.getFiles();
        let fileData;
        if (files && typeof files === 'object') {
          fileData = (files as any)[element.fileId];
        }
        
        if (fileData) {
          this.fileManager.addFile(element.fileId, fileData);
          
          const fileMessage = JSON.stringify({
            type: "image-file-update",
            fileId: element.fileId,
            fileData: fileData
          });
          
          try {
            this.sendMessage(fileMessage);
          } catch (error) {
            console.error(`[SYNC NEW IMAGES] Error calling sendMessage for fileId: ${element.fileId}:`, error);
          }
        }
      }
    });
  }

  loadAllExistingImages(): void {
    const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
    const imageElements = elements.filter(
      (element) => isInitializedImageElement(element) && !element.isDeleted
    ) as InitializedExcalidrawImageElement[];

    imageElements.forEach((element) => {
      if (!this.fileManager.isFileTracked(element.fileId)) {
        const files = this.excalidrawAPI.getFiles();
        let fileData;
        if (files && typeof files === 'object') {
          fileData = (files as any)[element.fileId];
        }
        
        if (fileData) {
          this.fileManager.addFile(element.fileId, fileData);
        }
      }
    });
  }

  reset(): void {
    this.fileManager.reset();
  }
} 